import {ApiError, Fetcher} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import bind from 'bind-decorator';
import {runInAction} from 'mobx';

export type Emoji = {
    name:string,
    char:string,
    category:string,
    keywords:Array<string>
}

export interface EmojiMap
{
    [key:string]:Emoji
}

export class EmojisController
{
    private fetcher:Fetcher;

    private emojis:Array<Emoji> = [];

    private emojiByName:EmojiMap;

    private emojiByGrapheme:EmojiTrie;

    private emojisLoaded:boolean = false;

    private emojisLoadingPromise:Promise<any> = null;

    public constructor()
    {
        this.fetcher = new Fetcher();
    }

    @bind
    public async getEmojis():Promise<Array<Emoji>>
    {
        if( this.emojisLoaded )
            return this.emojis;

        if( !this.emojisLoadingPromise )
            this.emojisLoadingPromise = this.fetcher.request('get', ConfigUtil.cdnEmojisFileUrl(), null);

        const emojisResponse = await this.emojisLoadingPromise;
        if( emojisResponse instanceof ApiError )
            throw new Error(emojisResponse.message);

        const emojisMap = emojisResponse as EmojiMap;
        const emojis = Object
            .entries(emojisMap)
            .map(([emojiName, emoji]) =>
            {
                emoji.name = emojiName;
                return emoji;
            });

        runInAction(() =>
        {
            const emojiByName = {};
            const emojiByGrapheme = new EmojiTrie();
            emojis.forEach(emoji =>
            {
                emojiByName[emoji.name] = emoji;
                emojiByGrapheme.put(emoji.char, emoji);
            });

            this.emojis = emojis;
            this.emojisLoaded = true;
            this.emojisLoadingPromise = null;
            this.emojiByName = emojiByName;
            this.emojiByGrapheme = emojiByGrapheme;
        });

        return emojis;
    }

    public async searchEmojis(searchText:string):Promise<Array<Emoji>>
    {
        const text = searchText.toLowerCase();

        const emojis = await this.getEmojis();
        return emojis.filter(({name, keywords}) =>
            name.indexOf(text) >= 0 || keywords.some(kw => kw.indexOf(text) >= 0)
        );
    }

    public async getEmojiByName(name:string):Promise<Emoji>
    {
        const emojiByName = await this.getEmojiByNameMap();
        return emojiByName[name];
    }

    public async getEmojiByNameMap():Promise<EmojiMap>
    {
        await this.getEmojis();
        return this.emojiByName;
    }

    public async getEmojiByGraphemeTrie():Promise<EmojiTrie>
    {
        await this.getEmojis();
        return this.emojiByGrapheme;
    }
}

type TrieChildren = {
    [codePoint:string]:EmojiTrie
}

class EmojiTrie
{
    private _children:TrieChildren = {};

    private _emoji:Emoji;

    private _depth:number;

    constructor(depth:number = 0)
    {
        this._depth = depth;
    }

    public put(key:string, emoji:Emoji)
    {
        let trie:EmojiTrie = this;

        const keyCodePoints = Array.from(key);
        for( let i = 0; i < keyCodePoints.length; ++i )
        {
            const codePoint = keyCodePoints[i];
            const children = trie._children;

            let child = children[codePoint];
            if( !child )
                child = children[codePoint] = new EmojiTrie(trie.depth + 1);

            trie = child;
        }

        trie._emoji = emoji;
    }

    public get(key:string):Emoji
    {
        const codePoints = Array.from(key);

        const child = this.findLongestEmoji(codePoints, 0);

        return child && child.depth === codePoints.length
            ? child.emoji
            : null;
    }

    public get depth():number
    {
        return this._depth;
    }

    public get emoji():Emoji
    {
        return this._emoji;
    }

    public findLongestEmoji(codePoints:Array<string>, startIndex:number):EmojiTrie
    {
        // search for an emoji grapheme, starting from startIndex codePoint
        let trie:EmojiTrie = this;
        let found:EmojiTrie = null;
        for( let i = startIndex; i < codePoints.length; ++i )
        {
            const child = trie._children[codePoints[i]];
            if( !child )
                break;

            if( child.emoji )
                found = child;

            trie = child;
        }

        return found;
    }
}
